// =============================================================================
// Shared room picker: search/list property rooms, or create new (with "Did you mean?").
// Used when "Add room" in Checklist or Inventory - single source of truth from Rooms tab.
// =============================================================================

import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, Plus, Loader2 } from "lucide-react";
import { usePropertyRooms, useCreatePropertyRoom, matchRoomAgainstProperty } from "@/hooks/usePropertyRooms";
import type { PropertyRoom } from "@/hooks/usePropertyRooms";
import { toast } from "@/hooks/use-toast";

export interface RoomPickerProps {
  propertyId: string;
  /** Single: pick one room. Multi: pick multiple (e.g. for checklist creation). */
  mode: "single" | "multi";
  /** For multi mode: pre-selected room ids. */
  selectedIds?: string[];
  /** Called when user selects room(s). Single: one room. Multi: array (including newly created). */
  onSelect: (rooms: PropertyRoom[]) => void;
  /** Optional: prompt text above the list */
  prompt?: string;
}

export function RoomPicker({ propertyId, mode, selectedIds = [], onSelect, prompt }: RoomPickerProps) {
  const { data: rooms = [], isLoading } = usePropertyRooms(propertyId);
  const createRoom = useCreatePropertyRoom();

  const [search, setSearch] = useState("");
  const [createExpanded, setCreateExpanded] = useState(false);
  const [newName, setNewName] = useState("");
  const [suggested, setSuggested] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return rooms;
    const q = search.trim().toLowerCase();
    return rooms.filter((r) => r.name.toLowerCase().includes(q));
  }, [rooms, search]);

  const handleAddNameChange = (value: string) => {
    setNewName(value);
    const match = matchRoomAgainstProperty(propertyId, rooms, value);
    setSuggested(match.suggested?.name ?? null);
  };

  const handleCreateNew = async (useExisting: boolean) => {
    if (useExisting && suggested) {
      const existing = rooms.find((r) => r.name === suggested);
      if (existing) {
        if (mode === "single") {
          onSelect([existing]);
        } else {
          const combined = [...rooms.filter((r) => selectedIds.includes(r.id)), existing];
          onSelect([...combined.filter((r, i, a) => a.findIndex(x => x.id === r.id) === i)]);
        }
      }
      setCreateExpanded(false);
      setNewName("");
      setSuggested(null);
      return;
    }
    const nameToUse = newName.trim();
    if (!nameToUse) {
      toast({ title: "Room name required", variant: "destructive" });
      return;
    }
    try {
      const created = await createRoom.mutateAsync({
        propertyId,
        name: nameToUse,
      });
      if (mode === "single") {
        onSelect([created]);
      } else {
        const existingSelected = rooms.filter((r) => selectedIds.includes(r.id));
        onSelect([...existingSelected, created]);
      }
      setCreateExpanded(false);
      setNewName("");
      setSuggested(null);
      toast({ title: "Room added", description: created.name });
    } catch (e) {
      toast({ title: "Failed to add room", description: (e as Error).message, variant: "destructive" });
    }
  };

  const handlePick = (room: PropertyRoom) => {
    if (mode === "single") {
      onSelect([room]);
    } else {
      const nextIds = selectedIds.includes(room.id)
        ? selectedIds.filter((id) => id !== room.id)
        : [...selectedIds, room.id];
      onSelect(rooms.filter((r) => nextIds.includes(r.id)));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading rooms…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {prompt && <p className="text-sm text-muted-foreground">{prompt}</p>}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search rooms..."
          className="pl-9"
        />
      </div>
      <ScrollArea className="h-[220px] rounded-md border p-2">
        <ul className="space-y-1">
          {filtered.map((room) => {
            const selected = selectedIds.includes(room.id);
            return (
              <li key={room.id}>
                <button
                  type="button"
                  onClick={() => handlePick(room)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    selected ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted"
                  }`}
                >
                  {mode === "multi" && (
                    <span className="mr-2">{selected ? "✓" : "○"}</span>
                  )}
                  {room.name}
                </button>
              </li>
            );
          })}
        </ul>
      </ScrollArea>
      <div>
        {!createExpanded ? (
          <Button type="button" variant="outline" size="sm" onClick={() => setCreateExpanded(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create new room
          </Button>
        ) : (
          <div className="space-y-2 rounded-md border p-3 bg-muted/30">
            <Label htmlFor="room-picker-new-name">New room name</Label>
            <Input
              id="room-picker-new-name"
              value={newName}
              onChange={(e) => handleAddNameChange(e.target.value)}
              placeholder="e.g. Kitchen"
            />
            {suggested && (
              <p className="text-sm text-amber-600">
                Did you mean &quot;{suggested}&quot;?{" "}
                <Button type="button" variant="link" className="p-0 h-auto text-sm" onClick={() => handleCreateNew(true)}>
                  Use existing
                </Button>
                {" · "}
                <Button type="button" variant="link" className="p-0 h-auto text-sm" onClick={() => { setSuggested(null); }}>
                  Create &quot;{newName.trim()}&quot; anyway
                </Button>
              </p>
            )}
            <div className="flex gap-2">
              <Button type="button" size="sm" onClick={() => handleCreateNew(false)} disabled={createRoom.isPending || !newName.trim()}>
                {createRoom.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create & add"}
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => { setCreateExpanded(false); setNewName(""); setSuggested(null); }}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
